import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SearchableSelect from './SearchableSelect';

const UserModal = ({ user, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'user',
    creditCardId: '',
    isActive: true,
    appAccess: { receipts: true, timeAudit: false },
    managedLocationIds: []
  });
  const [creditCards, setCreditCards] = useState([]);
  const [userCreditCards, setUserCreditCards] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedCardToAdd, setSelectedCardToAdd] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingCards, setLoadingCards] = useState(true);
  const isEditing = !!user;

  useEffect(() => {
    fetchCreditCards();
    fetchLocations();
  }, []);

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || '',
        password: '', // Don't show existing password
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        role: user.role || 'user',
        creditCardId: user.creditCardId || '',
        isActive: user.isActive !== undefined ? user.isActive : true,
        appAccess: user.appAccess || { receipts: true, timeAudit: false },
        managedLocationIds: user.managedLocationIds || []
      });
      // Fetch user's credit cards if editing
      fetchUserCreditCards(user.id);
    }
  }, [user]);

  const fetchCreditCards = async () => {
    try {
      setLoadingCards(true);
      const response = await axios.get('/api/credit-cards?includeInactive=false&limit=500');
      setCreditCards(response.data.creditCards);
    } catch (error) {
      console.error('Failed to fetch credit cards:', error);
      setError('Failed to load credit cards');
    } finally {
      setLoadingCards(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get('/api/locations?isActive=true');
      const locs = response.data.locations || response.data || [];
      // Sort by locationNumber ascending (lowest first)
      locs.sort((a, b) => {
        const numA = parseInt(a.locationNumber) || 0;
        const numB = parseInt(b.locationNumber) || 0;
        return numA - numB;
      });
      setLocations(locs);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const fetchUserCreditCards = async (userId) => {
    try {
      const response = await axios.get(`/api/admin/users/${userId}/credit-cards`);
      setUserCreditCards(response.data);
    } catch (error) {
      console.error('Failed to fetch user credit cards:', error);
    }
  };

  const handleAddCreditCard = async () => {
    if (!selectedCardToAdd || !user) return;

    try {
      await axios.post(`/api/admin/users/${user.id}/credit-cards`, {
        creditCardId: selectedCardToAdd
      });
      setSelectedCardToAdd('');
      fetchUserCreditCards(user.id);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to add credit card');
    }
  };

  const handleRemoveCreditCard = async (creditCardId) => {
    if (!user) return;

    if (!window.confirm('Are you sure you want to remove this credit card from the user?')) {
      return;
    }

    try {
      await axios.delete(`/api/admin/users/${user.id}/credit-cards/${creditCardId}`);
      fetchUserCreditCards(user.id);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to remove credit card');
    }
  };

  const handleSetPrimaryCard = async (creditCardId) => {
    if (!user) return;

    try {
      await axios.put(`/api/admin/users/${user.id}/credit-cards/${creditCardId}/primary`);
      fetchUserCreditCards(user.id);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to set primary card');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAppAccessChange = (app, checked) => {
    setFormData(prev => ({
      ...prev,
      appAccess: {
        ...prev.appAccess,
        [app]: checked
      }
    }));
  };

  const handleManagedLocationToggle = (locationId) => {
    setFormData(prev => {
      const currentIds = prev.managedLocationIds || [];
      if (currentIds.includes(locationId)) {
        return {
          ...prev,
          managedLocationIds: currentIds.filter(id => id !== locationId)
        };
      } else {
        return {
          ...prev,
          managedLocationIds: [...currentIds, locationId]
        };
      }
    });
  };

  const validateForm = () => {
    if (!formData.email.trim()) return 'Email is required';
    if (!isEditing && !formData.password.trim()) return 'Password is required';
    if (!formData.firstName.trim()) return 'First name is required';
    if (!formData.lastName.trim()) return 'Last name is required';
    
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      const submitData = { ...formData };
      
      // Convert empty string to null for creditCardId
      if (submitData.creditCardId === '') {
        submitData.creditCardId = null;
      }

      // Don't send password if it's empty during edit
      if (isEditing && !submitData.password) {
        delete submitData.password;
      }

      if (isEditing) {
        await axios.put(`/api/admin/users/${user.id}`, submitData);
        onSave('User updated successfully');
      } else {
        await axios.post('/api/admin/users', submitData);
        onSave('User created successfully');
      }
    } catch (error) {
      setError(error.response?.data?.error || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2>{isEditing ? 'Edit User' : 'Create New User'}</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              name="email"
              className="form-control"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Password {isEditing ? '(leave blank to keep current)' : '*'}</label>
            <input
              type="password"
              name="password"
              className="form-control"
              value={formData.password}
              onChange={handleChange}
              required={!isEditing}
            />
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label>First Name *</label>
              <input
                type="text"
                name="firstName"
                className="form-control"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label>Last Name *</label>
              <input
                type="text"
                name="lastName"
                className="form-control"
                value={formData.lastName}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Role</label>
            <SearchableSelect
              value={formData.role}
              onChange={(value) => setFormData({ ...formData, role: value })}
              options={[
                { id: 'user', label: 'User' },
                { id: 'viewer', label: 'Viewer (Read-Only)' },
                { id: 'admin', label: 'Admin' }
              ]}
              getOptionLabel={(opt) => opt.label}
              getOptionValue={(opt) => opt.id}
              placeholder="Select role"
              searchPlaceholder="Search roles..."
              allowEmpty={false}
            />
          </div>

          <div className="form-group">
            <label>App Access</label>
            <div style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '15px', backgroundColor: '#f9fafb' }}>
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.appAccess?.receipts !== false}
                    onChange={(e) => handleAppAccessChange('receipts', e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  Receipt Manager
                </label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.appAccess?.timeAudit === true}
                    onChange={(e) => handleAppAccessChange('timeAudit', e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  Time Audit
                </label>
              </div>
              <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '8px' }}>
                Select which applications this user can access.
              </small>
            </div>
          </div>

          {formData.appAccess?.timeAudit && (
            <div className="form-group">
              <label>Managed Locations (for Time Audit)</label>
              <div style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '15px', backgroundColor: '#f9fafb', maxHeight: '200px', overflowY: 'auto' }}>
                {locations.length === 0 ? (
                  <p style={{ color: '#666', margin: 0, fontSize: '14px' }}>No locations available.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {locations.map(location => (
                      <label
                        key={location.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                          padding: '8px',
                          backgroundColor: formData.managedLocationIds?.includes(location.id) ? '#e0e7ff' : 'white',
                          borderRadius: '4px',
                          border: '1px solid #ddd'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.managedLocationIds?.includes(location.id) || false}
                          onChange={() => handleManagedLocationToggle(location.id)}
                          style={{ marginRight: '10px' }}
                        />
                        <span>
                          <strong>{location.locationNumber}</strong>
                          {location.locationName && (
                            <span style={{ color: '#666', marginLeft: '8px' }}>
                              {location.locationName}
                            </span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '8px' }}>
                Select locations this user manages. They will receive notifications for time audit violations at these locations.
              </small>
            </div>
          )}

          <div className="form-group">
            <label>Credit Cards</label>
            {!isEditing ? (
              <p style={{ color: '#666', fontSize: '14px', margin: '10px 0' }}>
                Credit cards can be managed after creating the user.
              </p>
            ) : (
              <div style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '15px', backgroundColor: '#f9fafb' }}>
                {userCreditCards.length === 0 ? (
                  <p style={{ color: '#666', margin: '0 0 10px 0', fontSize: '14px' }}>
                    No credit cards assigned yet.
                  </p>
                ) : (
                  <div style={{ marginBottom: '15px' }}>
                    {userCreditCards.map((ucc) => (
                      <div
                        key={ucc.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '10px',
                          backgroundColor: 'white',
                          borderRadius: '4px',
                          marginBottom: '8px',
                          border: ucc.isPrimary ? '2px solid #4F46E5' : '1px solid #ddd'
                        }}
                      >
                        <div>
                          <strong>{ucc.CreditCard?.entity || 'Unknown'}</strong>
                          <span style={{ marginLeft: '8px', color: '#666' }}>
                            ••••{ucc.CreditCard?.lastFourCC || '????'}
                          </span>
                          {ucc.isPrimary && (
                            <span style={{
                              marginLeft: '8px',
                              padding: '2px 6px',
                              backgroundColor: '#4F46E5',
                              color: 'white',
                              borderRadius: '3px',
                              fontSize: '11px'
                            }}>
                              PRIMARY
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {!ucc.isPrimary && (
                            <button
                              type="button"
                              onClick={() => handleSetPrimaryCard(ucc.creditCardId)}
                              style={{
                                padding: '4px 8px',
                                fontSize: '12px',
                                backgroundColor: '#f3f4f6',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >
                              Set Primary
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleRemoveCreditCard(ucc.creditCardId)}
                            style={{
                              padding: '4px 8px',
                              fontSize: '12px',
                              backgroundColor: '#fee2e2',
                              border: '1px solid #fca5a5',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              color: '#dc2626'
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '8px' }}>
                  <div style={{ flex: 1 }}>
                    <SearchableSelect
                      value={selectedCardToAdd}
                      onChange={setSelectedCardToAdd}
                      options={creditCards.filter(card =>
                        !userCreditCards.some(ucc => ucc.creditCardId === card.id)
                      )}
                      getOptionLabel={(card) => `${card.entity} (••••${card.lastFourCC})`}
                      getOptionValue={(card) => card.id}
                      filterOption={(card, search) => {
                        const searchLower = search.toLowerCase();
                        return card.entity.toLowerCase().includes(searchLower) ||
                               card.lastFourCC.includes(searchLower);
                      }}
                      placeholder="Select a card to add..."
                      searchPlaceholder="Search credit cards..."
                      emptyLabel="Select Card"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleAddCreditCard}
                    disabled={!selectedCardToAdd}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: selectedCardToAdd ? '#4F46E5' : '#9ca3af',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: selectedCardToAdd ? 'pointer' : 'not-allowed'
                    }}
                  >
                    Add Card
                  </button>
                </div>
                <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                  Manage credit cards associated with this user. Users can have multiple cards.
                </small>
              </div>
            )}
          </div>

          {isEditing && (
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  style={{ marginRight: '8px' }}
                />
                Active User
              </label>
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button 
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : (isEditing ? 'Update User' : 'Create User')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserModal;